import { Injector } from '@angular/core';
import { ethers } from 'ethers';
import { Observable, ReplaySubject } from 'rxjs';

import { ePlatforms } from '@common/networks';
import { IToken } from '../api/token';
import { TransactionHistoryApi, TX_STATE } from '../api/transaction-history';
import { AppPasswordService } from '../app-password/app-password.service';
import { EVMErc20ContractFactory } from '../currencies-factory/evm-erc20-contract-factory.service';
import { NetworksService } from '../networks/networks.service';
import { PricesService } from '../prices/prices.service';
import { EVMProviderFactoryService } from '../provider/evm-provider-factory.service';
import { ITransactionParsed, ITransactionResponceEx } from './explorers/types';
import { IParseService } from './parser-tx/base-parse';
import { TransactionDirection, TransactionStatus } from './types';
import { UtilsService } from './utils.service';
import { Wallet } from './wallet.service';

// import { TransactionParserService } from './parser-tx/transaction-parser.service';

export interface ICurrency
{
    explorerLinkApi: string;

    scanUrl: string;

    isTestnet: boolean;

    contractAbi: string;

    getAddress(): string;

    getSymbol(): string;

    getFeeSymbol(): string;

    getFeeUnit(): string;

    getName(): string;

    getCurrencyId(): number;

    getPlatform(): ePlatforms;

    getPrice(
        currency: string,
        getBaseCoinPrice?: boolean
    ): Promise<{ price: string; price_change: string; }>;

    getFeeLimit(data: any, type?): Promise<string | any>;

    sendTransaction(
        tx: ethers.TransactionRequest,
        txInfo: { symbol?: string, value: string, to?: string, icon?: string; }
    ): Promise<any>;

    sendGasLessTx(
        tx: ethers.TransactionRequest,
        txInfo: { symbol?: string, value: string, paymaster: string, to?: string, icon?: string; }
    ): Promise<any>;

    checkTransaction(to: string, value: string): Promise<any>;

    getBalance(): Promise<any>;

    getBaseCurrencyBalance(): Promise<any>;

    populateTransaction(
        transaction: ethers.TransactionRequest,
        transfer?: boolean,
        gassless?: boolean
    ): Promise<any>;

    preparePrice(price: string): { price: any; };

    prepareFee(price: string, limit: string): { price: any; limit: any; };

    estimateFeePrice(to: string, value: string, transfer?: boolean): Promise<string>;

    getTransactionByHash(hash: string, parseData?: boolean, notCallContract?: boolean): Promise<ITransactionResponceEx>;

    parseTransaction(txData: ITransactionResponceEx, parseData?: boolean, notCallContract?: boolean): Promise<ITransactionParsed>;

    getHistory(): Promise<ITransactionResponceEx[]>;

    getTransactionDirection(tx: ITransactionResponceEx): TransactionDirection;

    getTransactionStatus(tx: any): Promise<TransactionStatus>;

    getWallet(): Wallet;

    getContractAddress(): string | null;

    checkFeeAvailability(feeData: any): Promise<{ result: boolean, amount?: string; }>;

    isFeeRequired(): Promise<boolean>;

    saveTxHistory(txResponse: ITransactionResponceEx, amount: string): Promise<any>;

    updateTxState(hash: string, txState: TX_STATE, failReason: string);

    mergeTxHistories(): Promise<ITransactionResponceEx[]>;

    updateTxHash(hash: string, newTxhash: string, feePrice: string, feeLimit: string);

    checkTxFail(hash: string);

    isCoinByChainId(chainId: number): boolean;

    getBlockchain(): string;

    getIconUrl(): string;

    getChainId(): number;

    getTokenAddress(): string;

    getIsToken(): boolean;

    getBlockInfo(blockNumber: number);

    getLinkToExplorer(txHash: string): string;

    getParser(): IParseService;

    getContractInstance(): ethers.Contract;

    getContractAddress(): string | null;

    getAbiErc(): Promise<ethers.InterfaceAbi>;

    getTransactionLogs(hash: string): Promise<any>;

    getTokenIcon(): string;
}


export abstract class Currency implements ICurrency
{
    protected name: string;
    protected symbol: string;
    protected id: number;
    protected baseSymbol: string;
    protected platform: ePlatforms;
    protected testnet: boolean;
    protected platformLower: string;
    protected currencyMarketData: any;
    protected address: string;
    protected provider: ethers.Provider;
    protected abi: string;
    protected contract: ethers.Contract;
    protected history: ITransactionResponceEx[] = [];
    protected wallet: Wallet;
    protected networksService: NetworksService;
    protected providerFactoryService: EVMProviderFactoryService;
    protected passwordService: AppPasswordService;
    protected pricesService: PricesService;
    protected parserService: IParseService;
    protected contractFactory: EVMErc20ContractFactory;
    protected txHistoryApi: TransactionHistoryApi;
    protected etherUtils: UtilsService;
    protected chainId: number;
    protected blockchain: string;
    protected iconUrl: string;
    protected explorerLink: string;
    protected tokenAddress: string;
    protected scanLink: string;
    protected isToken: boolean;
    protected tokenIcon: string;

    protected setupStateSubject: ReplaySubject<boolean> = new ReplaySubject(1);

    constructor(
        protected injector: Injector,
        _wallet: Wallet,
        _address: string,
        _symbol: string,
        _dbToken: IToken,
        _id?: number
    )
    {
        this.wallet = _wallet;
        this.id = _id || null;
        this.symbol = _symbol;
        this.address = _address;
        this.name = _dbToken.name;
        this.testnet = _dbToken.blockchainMain.testnet;
        this.iconUrl = _dbToken.blockchainMain.icon;
        this.blockchain = _dbToken.blockchainMain.name;
        this.explorerLink = _dbToken.blockchainMain.explorer;
        this.chainId = _dbToken.blockchainMain.chain_id;
        this.networksService = this.injector.get(NetworksService);
        this.providerFactoryService = this.injector.get(EVMProviderFactoryService);
        this.passwordService = this.injector.get(AppPasswordService);
        this.pricesService = this.injector.get(PricesService);
        this.txHistoryApi = this.injector.get(TransactionHistoryApi);
        this.etherUtils = this.injector.get(UtilsService);
        this.contractFactory = this.injector.get(EVMErc20ContractFactory);
        this.tokenIcon = _dbToken.icon;
    }

    protected abstract init(): Promise<any>;
    public abstract checkTransaction(_to: string, _value: string): Promise<any>;
    public abstract getBalance(): Promise<any>;
    public abstract getBaseCurrencyBalance(): Promise<any>;
    public abstract checkFeeAvailability(_feeData: any): Promise<{ result: boolean; amount?: string; }>;
    public abstract getTransactionByHash(_hash: string, _parseData?: boolean, _notCallContract?: boolean): Promise<ITransactionResponceEx>;
    public abstract getTransactionLogs(_hash: string): Promise<any>;
    public abstract getTransactionDirection(_tx: ITransactionResponceEx): TransactionDirection;
    public abstract parseTransaction(_txData: ITransactionResponceEx, _parseData?: boolean, _notCallContract?: boolean): Promise<ITransactionParsed>;
    public abstract isFeeRequired(): Promise<boolean>;
    public abstract estimateFeePrice(to: string, _value: string, _transfer?: boolean): Promise<string>;
    public abstract getHistory(): Promise<ITransactionResponceEx[]>;
    public abstract getTransactionStatus(_tx: any): Promise<TransactionStatus>;
    public abstract getFeeLimit(_type?): Promise<string | any>;
    public abstract sendTransaction(
        _tx: ethers.TransactionRequest,
        _txInfo: { symbol?: string, value: string, to?: string; }
    ): Promise<any>;
    public abstract sendGasLessTx(
        _tx: ethers.TransactionRequest,
        _txInfo: { symbol?: string, value: string, paymaster: string, to?: string; }
    ): Promise<any>;
    public abstract populateTransaction(
        _transaction: ethers.TransactionLike,
        _transfer?: boolean,
        _gassless?: boolean
    ): Promise<any>;
    public abstract saveTxHistory(_txResponse: ITransactionResponceEx, amount: string): Promise<any>;
    public abstract updateTxState(_hash: string, _txState: TX_STATE, _failReason: string);
    public abstract mergeTxHistories(): Promise<ITransactionResponceEx[]>;
    public abstract updateTxHash(_hash: string, _newTxhash: string, _feePrice: string, _feeLimit: string);
    public abstract checkTxFail(_hash: string): Promise<any>;
    public abstract isCoinByChainId(_chainId: number): boolean;
    public abstract getBlockInfo(_blockNumber: number);
    public abstract getLinkToExplorer(_txHash: string): string;
    public abstract getPrice(_currency: string, _getBaseCoinPrice?: boolean): Promise<{ price: string; price_change: string; }>;

    public getAddress(): string
    {
        return this.address;
    }

    public getCurrencyId(): number
    {
        return this.id;
    }

    public getSymbol(): string
    {
        return this.symbol;
    }

    public getTokenIcon(): string
    {
        return this.tokenIcon;
    }

    public getFeeSymbol(): string
    {
        return this.baseSymbol;
    }

    public getName(): string
    {
        return this.name;
    }

    public getPlatform(): ePlatforms
    {
        return this.platform;
    }

    public getBlockchain(): string
    {
        return this.blockchain;
    }

    public getChainId(): number
    {
        return this.chainId;
    }

    public getFeeUnit(): string
    {
        return 'gwei';
    }

    get onSetup(): Observable<boolean>
    {
        return this.setupStateSubject.asObservable();
    }

    public preparePrice(_price: string): { price: any; }
    {
        return {
            price: ethers.parseUnits(_price.split('.')[ 0 ], 'gwei'),
        };
    }

    public prepareFee(
        _price: string,
        _limit: string
    ): { price: any; limit: any; }
    {
        return {
            price: ethers.parseUnits(_price.split('.')[ 0 ], 'gwei'),
            limit: ethers.parseUnits(_limit.split('.')[ 0 ], 'wei'),
        };
    }

    public getWallet(): Wallet
    {
        return this.wallet;
    }

    public getIconUrl(): string
    {
        return this.iconUrl;
    }

    public getTokenAddress(): string
    {
        return this.tokenAddress;
    }

    public getIsToken(): boolean
    {
        return this.isToken;
    }

    get explorerLinkApi(): string
    {
        return this.explorerLink;
    }

    get scanUrl(): string
    {
        return this.scanLink;
    }

    get isTestnet(): boolean
    {
        return Boolean(this.testnet);
    }

    get contractAbi(): string
    {
        return this.abi;
    }

    public getParser(): IParseService
    {
        return this.parserService;
    }

    public getContractAddress(): string | null
    {
        return this.tokenAddress;
    }

    public getContractInstance(): ethers.Contract
    {
        return this.contract;
    }

    public async getAbiErc(): Promise<ethers.InterfaceAbi>
    {
        return await this.contractFactory.loadContractABI();
    }
}
